import React, { useState, useRef, FC } from 'react';
import { KiteIcon } from '@kite/react-kite';
import { ImageDataLike, GatsbyImage, getImage } from 'gatsby-plugin-image';
import { getVideoId } from '../../shared/getVideoId';
import { CURRENT_SITE } from '../../../config-sites';
import { graphql, useStaticQuery } from 'gatsby';
import './SectionVideoCardVideo.scss';

export interface IVideoBase {
    /** The video title */
    title: string;
    /** The video source URL */
    videoSrc: string;
    /** The image that overlays the video element until it's played */
    posterImg?: {
        localFile: ImageDataLike;
        alt: string;
    };
}
export const SectionVideoCardVideo: FC<IVideoBase> = ({
    posterImg,
    title,
    videoSrc,
}: IVideoBase) => {
    const url = new URL(videoSrc, CURRENT_SITE.siteUrl);
    const isYoutube =
        url.hostname?.includes(`youtube.com`) ||
        url.hostname?.includes(`youtu.be`);
    const videoId = isYoutube ? getVideoId(videoSrc) : undefined;
    const [hideOverlay, setHideOverlay] = useState<boolean>(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Retrieves the fallback video poster image to use if not provided by the author.
    const fallbackQuery = useStaticQuery(graphql`
        query image {
            allMediaImage(
                filter: {
                    name: {
                        eq: "Default Video Card Poster Image Leadership Bio V3 - Do Not Remove"
                    }
                }
            ) {
                nodes {
                    field_media_image {
                        alt
                    }
                    relationships {
                        field_media_image {
                            localFile {
                                childImageSharp {
                                    ...ImageFullWidthSettings
                                }
                            }
                        }
                    }
                }
            }
        }
    `);

    const fallbackImageData = {
        localFile:
            fallbackQuery?.allMediaImage.nodes?.[0]?.relationships
                ?.field_media_image?.localFile,
        alt: fallbackQuery?.allMediaImage.nodes?.[0]?.field_media_image?.alt,
    };

    const imageData = posterImg?.localFile ?? fallbackImageData.localFile;
    const imageAlt = posterImg?.alt ?? fallbackImageData.alt;

    const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        const video = videoRef.current;
        if (video) {
            setHideOverlay(true);
            // eslint-disable-next-line no-console
            video.play().catch(console.error);
        }
    };

    const handleOverlayClick = () => {
        const button = buttonRef.current;
        if (button) {
            button.click();
        }
    };

    if (isYoutube) {
        return (
            <iframe
                src={`https://www.youtube.com/embed/${videoId}?enablejsapi=1&rel=0`}
                title={title ? `${title} video player` : `YouTube video player`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="video-base video-base--iframe"
            />
        );
    }
    return (
        <div className="video-base" aria-label="video player">
            <video
                ref={videoRef}
                title={title}
                // hides the controls when the overlay image is preset to avoid hidden tab focus
                controls={!!hideOverlay}
                className={`video-base video-base--video ${
                    hideOverlay ? `` : `video-base--hidden`
                }`}
                aria-hidden={!hideOverlay}
            >
                <source src={videoSrc} />
                <p>
                    Your browser doesn't support HTML video. Here is a
                    <a href={videoSrc} download>
                        link to download the video
                    </a>
                    instead.
                </p>
            </video>

            <div
                className={`video-base__overlay ${hideOverlay ? `hidden` : ``}`}
                onClick={handleOverlayClick}
            >
                <GatsbyImage image={getImage(imageData)} alt={imageAlt} />
                <button
                    onClick={handleButtonClick}
                    aria-label={`Play ${title} video`}
                    title="Play"
                    ref={buttonRef}
                >
                    <PlayButtonBg />
                    <KiteIcon icon="ki-play-circle" size="3rem" />
                </button>
            </div>
        </div>
    );
};

const PlayButtonBg = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="42"
        height="42"
        viewBox="0 0 42 42"
        fill="none"
        className="play-btn-bg"
        aria-hidden
    >
        <circle cx="21" cy="21" r="21" fill="black" fill-opacity="0.6" />
    </svg>
);
